import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-numberformat/locale-data/ka';
import '@formatjs/intl-numberformat/locale-data/en';

import { IntlProvider } from 'react-intl';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import {
  defaultLocale,
  LANGUAGE_SWITCHING_ENABLED,
  LOCALE_STORAGE_KEY,
  type Locale,
  locales,
} from './config';
import { messagesByLocale } from './messages';

function isSupportedLocale(locale: string): locale is Locale {
  return (locales as readonly string[]).includes(locale);
}

function getStoredLocale(): Locale | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && isSupportedLocale(stored)) {
    return stored;
  }
  return null;
}

function storeLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within an I18nProvider');
  }
  return context;
}

export interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (!LANGUAGE_SWITCHING_ENABLED) return defaultLocale;
    const stored = getStoredLocale();
    if (stored) return stored;
    return defaultLocale;
  });

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    storeLocale(newLocale);
  }, []);

  const messages = messagesByLocale[locale];

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <IntlProvider locale={locale} messages={messages} defaultLocale={defaultLocale}>
        <>{children}</>
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
